import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import Layout, { Empty } from '@/components/Layout'
import { Badge, Banner, Button, Field, Fold, List, Row, Section, fieldInputClass } from '@/components/ui'
import DateStrip, { type StripDay } from '@/components/DateStrip'
import ProgressRing from '@/components/ProgressRing'
import { Sheet, SheetPad } from '@/components/Sheet'
import ReviewQueue from '@/components/ReviewQueue'
import { toast } from '@/components/Toast'
import { useCanWrite } from '@/lib/access'
import type { SharedProps, TaskFull } from '@/types'
import { t } from '@/lib/i18n'

// 稿子（docs/designs/mobile/app-checkin.html）里每行右侧有一个 44px 的 📷
// 「打卡并加附件」，已完成行的中段是附件入口，toast 上还有「📷 加照片」第二动作位。
// **这三处一律不渲染**：仓库里没有 ActiveStorage（`grep -c active_storage db/schema.rb` = 0），
// 那套面板落不了地。留一个点不动的按钮比没有更糟。
// 要做得先接 ActiveStorage + 存储桶 + 配额，那是独立一片。
//

interface Props {
  date_label: string
  viewed_date: string
  max_date: string
  is_today: boolean
  streak: number
  earned_today: number
  total_tasks: number
  done_count: number
  avail: TaskFull[]
  done: TaskFull[]
  day_counts: Record<string, number>
  pending_claims: PendingClaim[]
  // 作业入口卡的条数。recorded=false 时显「还没录 · 粘一段」。
  homework: { recorded: boolean; total: number; done: number }
  // 只有申报权限（孩子）：看到「申报」而不是「打卡」，且看不到审核按钮
  can_claim_only: boolean
  // 「还差一点」——最接近达成的目标；两档文案互斥，都不满足就是 null
  almost: { kind: 'today' | 'points'; title: string; left: number } | null
}

interface PendingClaim {
  id: number
  task_title: string
  points: number
  on: string
  claimed_by: string
  at: string
}

export default function Index({
  date_label, viewed_date, max_date, is_today, streak, earned_today, total_tasks, done_count,
  avail, done, day_counts, pending_claims, homework, can_claim_only, almost,
}: Props) {
  const canWrite = useCanWrite()
  const locale = usePage<SharedProps>().props.locale
  const left = total_tasks - done_count

  // 驳回哪一条。null = sheet 关着。存整条而不是 id：sheet 的标题要显示步骤名，
  // 只存 id 就得在渲染时再去列表里找一遍。
  const [rejecting, setRejecting] = useState<PendingClaim | null>(null)
  const [reason, setReason] = useState('')

  // ── 三个写操作 ──
  //
  // toast 放 onSuccess 而不是点击那一刻：点击时还不知道服务端会不会拒（目标刚被
  // 别人归档、步骤刚被删），先说「+3 分 · 撤销」再回滚是在撒谎。
  //
  // 稿子里的 `.float` 冒分与 `.justdone` 缩放这一版**不做**：它们要一个指向具体行
  // DOM 的 ref，而 Inertia 局部刷新会把那一行整个换掉。打卡的即时感由 toast 承担，
  // 行从待完成消失本身就是反馈。真要补动画是独立一片。
  const checkin = (task: TaskFull) => {
    router.post(`/checkin/${task.id}`, { date: viewed_date }, {
      preserveScroll: true,
      onSuccess: () => toast(
        t('checkin.toast_done', { title: task.title, count: task.points }),
        { undo: () => undo(task) },
      ),
    })
  }

  const undo = (task: TaskFull) => {
    router.delete(`/checkin/${task.id}`, {
      data: { date: viewed_date },
      preserveScroll: true,
      onSuccess: () => toast(t('checkin.toast_undone', { title: task.title })),
    })
  }

  // 孩子（claim 权限）提交的是申报，不直接加分，所以**不给撤销**——分从来没加上去过，
  // 而 TaskClaim 没有 destroy 路由。toast 里如实说「等家长确认」。
  const claim = (task: TaskFull) => {
    router.post('/task_claims', { task_id: task.id }, {
      preserveScroll: true,
      onSuccess: () => toast(t('checkin.toast_claimed', { title: task.title })),
    })
  }

  const approve = (c: PendingClaim) => {
    router.post(`/task_claims/${c.id}/approve`, {}, {
      preserveScroll: true,
      onSuccess: () => toast(t('checkin.toast_approved', { title: c.task_title, count: c.points })),
    })
  }

  const reject = () => {
    const c = rejecting!
    setRejecting(null)
    router.delete(`/task_claims/${c.id}/approve`, {
      data: { reason },
      preserveScroll: true,
      onSuccess: () => { setReason(''); toast(t('checkin.toast_rejected', { title: c.task_title })) },
    })
  }

  // ── 日期条 ──
  // 星期短名交给 Intl：自己拼「周一/周二」会把语序和语言锁死在中文上，而这个仓库
  // 是双语的（同 CheckinsController 里 date_label 用 I18n.l 的理由）。
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' })
  const days: StripDay[] = Object.keys(day_counts).sort().map((date) => {
    // 'YYYY-MM-DD' 直接进 new Date() 会被当成 UTC 午夜，在东八区显示成前一天。
    // 补 T00:00 让它按本地时区解析。
    const d = new Date(`${date}T00:00`)
    return {
      date,
      label: date === max_date ? t('checkin.strip_today') : weekday.format(d),
      day: d.getDate(),
      count: day_counts[date],
    }
  })

  // ── 待完成按目标分组 ──
  // 用 Map 而不是普通对象：对象的数字 key 会被 JS 重排成升序，于是目标顺序会变成
  // 「id 从小到大」而不是服务端给的顺序（服务端是按目标、再按步骤顺序摊平的）。
  //
  // 组标题只有目标名、没有 emoji：goals 表没有 emoji 列，而人本来就把 emoji 写进了
  // 标题（种子数据的「📖 每天阅读 30 分钟」）。为这个加一列不值得。
  const groups = new Map<number, { title: string; tasks: TaskFull[] }>()
  avail.forEach((task) => {
    const group = groups.get(task.goal_id) ?? { title: task.goal_title, tasks: [] }
    group.tasks.push(task)
    groups.set(task.goal_id, group)
  })

  return (
    <Layout bleed>
      <DateStrip
        days={days}
        selected={viewed_date}
        onSelect={(date) => router.get('/checkin', { date }, { preserveScroll: true })}
      />

      {!is_today && (
        <Banner tone="amber" icon="🗓️">
          {t('checkin.makeup_banner', { date: date_label })}
        </Banner>
      )}

      {almost && (
        <Banner tone="brand" icon="🎯">
          {almost.kind === 'today'
            ? t('checkin.almost_today', { title: almost.title })
            : t('checkin.almost_points', { left: almost.left, title: almost.title })}
        </Banner>
      )}

      {/* 家长打开这一页第一眼要看到的是「等我处理的东西」，不是自己的进度环。
          孩子看不到这一段——pending_claims 服务端无条件下发（所以它才不吐邮箱），
          但审核自己的申报没有意义。
          <ReviewQueue> 与作业页的「等签名」段是**同一个组件**：家长睡前要处理的
          是一个队列，不是两个（作业设计稿 §六②）。 */}
      {!can_claim_only && canWrite && (
        <ReviewQueue
          title={t('checkin.pending_title')}
          count={pending_claims.length}
          hint={t('checkin.scored_after_confirm')}
          rows={pending_claims.map((c) => ({
            id: c.id,
            title: c.task_title,
            sub: t('checkin.claimed_by', { name: c.claimed_by }),
            pts: `+${c.points}`,
            onApprove: () => approve(c),
            approveLabel: t('checkin.confirm_aria', { title: c.task_title }),
            onReject: () => { setReason(''); setRejecting(c) },
            rejectLabel: t('checkin.reject'),
            rejectAriaLabel: t('checkin.reject_aria', { title: c.task_title }),
          }))}
        />
      )}

      {/* 作业不占底部 tab（作业设计稿 §二）：作业是「打卡」的一种，不是第五个
          平级概念，而 tab bar 那 5 格已经满了。入口就是这张卡。 */}
      <Link
        href={`/homework?date=${viewed_date}`}
        className="flex items-center gap-3 mx-3 mt-3 p-3.5 min-h-tap bg-card rounded-card shadow-card active:bg-sunk"
      >
        <span aria-hidden="true" className="w-[38px] h-[38px] shrink-0 rounded-[12px] grid place-items-center text-[19px] bg-tint">📋</span>
        <span className="flex-1 min-w-0">
          <b className="block text-[15px]">{t('homework.entry_title')}</b>
          <span className="block text-faint text-xs mt-0.5">
            {homework.recorded
              ? t('homework.entry_progress', { done: homework.done, total: homework.total })
              : t('homework.entry_empty')}
          </span>
        </span>
        {homework.recorded && homework.done < homework.total && (
          <Badge tone="amber">{homework.total - homework.done}</Badge>
        )}
        <span aria-hidden="true" className="shrink-0 text-faint text-[17px]">›</span>
      </Link>

      <section className="mx-3 mt-3 p-4 bg-card rounded-card shadow-card flex items-center gap-4">
        <ProgressRing done={done_count} total={total_tasks} />
        <div className="min-w-0">
          <h1 className="m-0 mb-[3px] text-[17px] font-bold">
            {is_today
              ? (left > 0 ? t('checkin.hero_left', { count: left }) : t('checkin.hero_all_done'))
              : (left > 0 ? t('checkin.hero_left_that_day', { count: left }) : t('checkin.hero_all_done_that_day'))}
          </h1>
          <p className="m-0 text-muted text-[13px]">
            {done_count > 0 ? t('checkin.hero_earned', { count: earned_today }) : t('checkin.hero_nudge')}
          </p>
          {/* 连击 0 天时不显示：「连续 0 天」是一句没有信息的话，
              而空胶囊会让新用户以为自己漏了什么。 */}
          {streak > 0 && (
            <span className="inline-flex items-center gap-1 mt-[7px] px-[9px] py-[3px] rounded-full bg-amber-tint text-amber-strong text-xs font-bold">
              🔥 {t('checkin.hero_streak', { count: streak })}
            </span>
          )}
        </div>
      </section>

      {avail.length === 0 ? (
        <Empty
          emoji="🎉"
          title={is_today ? t('checkin.empty_title_today') : t('checkin.empty_title_that_day')}
          action={<Button variant="ghost" onClick={() => router.visit('/goals')}>{t('checkin.empty_cta')}</Button>}
        >
          {t('checkin.empty_sub', { count: earned_today })}
        </Empty>
      ) : (
        [...groups].map(([goalId, group]) => (
          <Section key={goalId} title={group.title} tail={t('checkin.group_left', { count: group.tasks.length })}>
            <List>
              {group.tasks.map((task) => (
                <Row
                  key={task.id}
                  lead={task.stars}
                  title={task.title}
                  pts={`+${task.points}`}
                  // 只读成员两个都不满足 ⇒ onTap 为 undefined ⇒ Row 渲染成不可点的 span。
                  // 那正是要的行为：点了没反应比点了被服务端弹回首页好。
                  onTap={can_claim_only ? () => claim(task) : canWrite ? () => checkin(task) : undefined}
                  ariaLabel={
                    can_claim_only
                      ? t('checkin.row_aria_claim', { title: task.title })
                      : t('checkin.row_aria_checkin', { title: task.title, count: task.points })
                  }
                />
              ))}
            </List>
          </Section>
        ))
      )}

      {done.length > 0 && (
        <Fold
          defaultOpen={avail.length === 0}
          summary={t('checkin.fold_done', { count: done.length, points: earned_today })}
        >
          <List>
            {done.map((task) => (
              <Row
                key={task.id}
                done
                lead="✅"
                title={task.title}
                pts={`+${task.points}`}
                right={canWrite ? (
                  <button
                    type="button"
                    onClick={() => undo(task)}
                    aria-label={t('checkin.undo_aria', { title: task.title })}
                    className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-[13px] font-semibold active:bg-sunk active:text-red"
                  >
                    {t('checkin.undo')}
                  </button>
                ) : undefined}
              />
            ))}
          </List>
        </Fold>
      )}

      {/* 驳回理由：取代 window.prompt（交接稿 §4④ 点名要干掉的就是这一处）。
          理由可以留空（服务端本来就接受空字符串），所以主按钮不 disable。 */}
      <Sheet
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        label={t('checkin.reject')}
        title={t('checkin.reject_title', { title: rejecting?.task_title ?? '' })}
      >
        <SheetPad>
          <Field label={t('checkin.reject_label')}>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('checkin.reject_placeholder')}
              className={fieldInputClass}
            />
          </Field>
          <div className="grid gap-2">
            <Button variant="danger" onClick={reject}>{t('checkin.reject_submit')}</Button>
            <Button variant="plain" onClick={() => setRejecting(null)}>{t('checkin.reject_cancel')}</Button>
          </div>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}
